import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { getFreeExtras, saveFreeExtras } from "@/lib/free-extras-api";
import { FREE_EXTRAS_COPY } from "@/lib/free-extras";
import { redirectToAuthIfRequired } from "@/lib/auth-required-redirect";
import { useI18n } from "@/lib/i18n";

export default function FreeExtrasSection() {
  const { locale, tx } = useI18n();
  const copy = FREE_EXTRAS_COPY[locale];
  const navigate = useNavigate();
  const location = useLocation();
  const [enabled, setEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await getFreeExtras();
      if (typeof payload?.enabled !== "boolean") throw new Error("Invalid preference response");
      if (mounted.current) setEnabled(payload.enabled);
    } catch (failure) {
      if (mounted.current && !redirectToAuthIfRequired(failure, { navigate, location })) setError("loadError");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [navigate, location]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => { mounted.current = false; };
  }, [load]);

  async function changeEnabled(nextEnabled) {
    setSaving(true);
    setError("");
    try {
      const payload = await saveFreeExtras(nextEnabled);
      if (typeof payload?.enabled !== "boolean") throw new Error("Invalid preference response");
      if (mounted.current) setEnabled(payload.enabled);
    } catch (failure) {
      if (mounted.current && !redirectToAuthIfRequired(failure, { navigate, location })) setError("saveError");
    } finally {
      if (mounted.current) setSaving(false);
    }
  }

  return (
    <section className="rounded-3xl border bg-card p-6" aria-labelledby="free-extras-heading">
      <div className="flex items-center justify-between gap-4">
        <h2 id="free-extras-heading" className="text-lg font-semibold">{copy.title}</h2>
        {enabled !== null && !loading ? (
          <label className="flex min-h-11 min-w-11 items-center justify-center">
            <Switch aria-labelledby="free-extras-heading" aria-describedby="free-extras-description free-extras-consequence"
              checked={enabled} disabled={saving} onCheckedChange={(next) => void changeEnabled(next)} />
          </label>
        ) : null}
      </div>
      <p id="free-extras-description" className="mt-2 text-sm text-muted-foreground">{copy.description}</p>
      <p id="free-extras-consequence" className="mt-2 text-sm text-muted-foreground">{copy.keepGift}</p>
      {loading || saving ? <p role="status" className="mt-3 text-sm text-muted-foreground">{tx(loading ? "Loading…" : "Saving…")}</p> : null}
      {error ? <p role="alert" className="mt-3 text-sm text-destructive">{copy[error]}</p> : null}
      {error === "loadError" ? <Button variant="outline" className="mt-3 min-h-11" onClick={() => void load()}>{tx("Try again")}</Button> : null}
    </section>
  );
}
